import { getCookie, removeCookie } from "@insite/client-framework/Common/Cookies";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import { productAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { BrandDetailsPageContext } from "@insite/content-library/Pages/BrandDetailsPage";
import { OrderConfirmationPageContext } from "@insite/content-library/Pages/OrderConfirmationPage";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import { useBrand } from "@store/Hooks/Brand";
import useOrderConfirmationCart from "@store/Hooks/Cart/useOrderConfirmationCart";
import { useCatalogPage, useCategory, useParentCategories } from "@store/Hooks/Category";
import { useBloomreachSettings, useCurrentLanguage, useWebsiteName, useWebsiteSettings } from "@store/Hooks/Context";
import useCurrentCurrency from "@store/Hooks/Context/useCurrentCurrency";
import useUserId from "@store/Hooks/Context/useUserId";
import { useCurrentPage, useCurrentPath, useCurrentSearch } from "@store/Hooks/Pages";
import { useProduct } from "@store/Hooks/Product";
import { useIsCategoryPage, useIsSearchPage, useSearchQuery } from "@store/Pages/ProductList/Hooks";
import { generatePageTitle } from "@store/utilities/generatePageTitle";
import { FC, useEffect, useState } from "react";

export interface BasketItem {
    prod_id: string;
    sku: string;
    name: string;
    quantity: number;
    price: number;
}

const BloomreachPixel: FC = () => {
    const [isFirst, setIsFirst] = useState(true);
    const [isWaitingForState, setIsWaitingForState] = useState(false);

    // Common Data
    const bloomreachSettings = useBloomreachSettings();
    if (!bloomreachSettings) {
        return null;
    }
    const currentCurrency = useCurrentCurrency();
    const currentLanguage = useCurrentLanguage();
    const userId = useUserId() || "";
    const websiteName = useWebsiteName();
    const websiteSettings = useWebsiteSettings();

    const currentPath = useCurrentPath();
    const isHomePage = currentPath === "/";
    const currentSearch = useCurrentSearch();
    const currentPage = useCurrentPage();

    // Category Data
    const isCategoryPage = useIsCategoryPage();
    const catalogPage = useCatalogPage();
    const category = useCategory();
    const parentCategories = useParentCategories();

    // Product Data
    const product = useProduct();

    // Search Data
    const isSearchPage = useIsSearchPage();
    const searchQuery = useSearchQuery();

    // Brand Data
    const brand = useBrand();

    // Conversion Data
    const orderConfirmationCart = useOrderConfirmationCart();

    // Other Common Data

    const getPageTitle = () => {
        let title = currentPage.fields.title;
        if (catalogPage) {
            title = catalogPage?.title || category?.name;
        }
        if (isSearchPage) {
            title = translate("Search Results");
        }
        if (product) {
            title = product.content?.pageTitle || product.productTitle;
        }
        if (brand) {
            title = brand.pageTitle || brand.name;
        }
        const actualTitle = generatePageTitle(title || "", websiteName, websiteSettings);
        return actualTitle;
    };

    const getDomainKey = () => {
        return bloomreachSettings?.domainKey;
    };

    const fillProductData = (brData: any) => {
        if (product && currentLanguage) {
            brData.ptype = "product";
            brData.prod_id = product.id + currentLanguage.languageCode;
            brData.prod_name = product.productTitle;
            brData.sku = product.properties.fullErpNumber;
            return true;
        }
        return false;
    };

    const fillCategoryData = (brData: any) => {
        if (category) {
            brData.ptype = "category";
            brData.cat_id = category?.id;
            brData.cat = parentCategories?.join("|");
            return true;
        }
        return false;
    };
    const fillConversionData = (brData: any) => {
        if (orderConfirmationCart && currentLanguage) {
            const conversionOrderCookie = getCookie(`ConversionOrderConfirmation_${orderConfirmationCart.id}`);
            if (!conversionOrderCookie) {
                // Poolcorp Custom Code: we want this conversionPageView hit only once.
                // By returning true, without adding data, a normal pageView will occur
                // if the conversionPageView for this order has already been tracked.
                return true;
            }
            removeCookie(`ConversionOrderConfirmation_${orderConfirmationCart.id}`);
            brData.ptype = "other";
            brData.is_conversion = 1;
            brData.basket_value = orderConfirmationCart.orderGrandTotal;
            brData.order_id = orderConfirmationCart.erpOrderNumber || orderConfirmationCart.orderNumber;
            const basketItems: BasketItem[] = [];
            orderConfirmationCart.cartLines?.forEach(cartline => {
                if (cartline) {
                    basketItems.push(({
                        prod_id: cartline.productId + currentLanguage.languageCode,
                        sku: cartline.properties.fullErpNumber,
                        name: cartline.shortDescription,
                        quantity: cartline.qtyOrdered,
                        price: cartline.pricing?.unitNetPrice,
                    } as unknown) as BasketItem);
                }
            });
            brData.basket = {
                items: basketItems,
            };
            return true;
        }
        return false;
    };

    const fillSearchData = (brData: any) => {
        if (searchQuery) {
            brData.ptype = "search";
            brData.search_term = searchQuery;
            return true;
        }
        return false;
    };

    const getBloomreachData = (currentPage: PageProps) => {
        const brData: any = {}; // Global Data

        let result = false;
        const pageTitle = getPageTitle();
        if (!pageTitle) {
            setIsWaitingForState(true);
            return false;
        }

        // Global tracking parameters
        brData.acct_id = bloomreachSettings?.accountId;
        brData.ptype = isHomePage ? "homepage" : "other"; // "homepage", "other" if Conversion / Thank You / Other
        brData.title = pageTitle;
        brData.domain_key = getDomainKey();
        brData.user_id = userId;
        brData.view_id = bloomreachSettings.defaultViewId;
        brData.currency = currentCurrency?.currencyCode ?? "";
        brData.tms = "";
        brData.ref = window.location.href;

        if (bloomreachSettings?.pixelTestData) {
            brData.test_data = "true";
        }

        // INSERT the specific Tracking Parameters in your Global Tracking Script
        switch (currentPage.type) {
            case ProductDetailsPageContext:
                result = fillProductData(brData);
                break;

            case ProductListPageContext:
                if (isSearchPage) {
                    result = fillSearchData(brData);
                } else if (isCategoryPage) {
                    result = fillCategoryData(brData);
                }
                break;

            case "CategoryDetailsPage":
                result = fillCategoryData(brData);
                break;

            case BrandDetailsPageContext:
                result = !!brand;
                break;

            case OrderConfirmationPageContext:
                result = fillConversionData(brData);
                break;

            default:
                result = true;
                break;
        }
        // END - page specific tracking parameters

        if (!result) {
            setIsWaitingForState(true);
            return false;
        }
        return brData;
    };

    const trackVirtualPageView = () => {
        const tmpBrData = getBloomreachData(currentPage);

        if (tmpBrData) {
            tmpBrData.orig_ref_url = window.location.href;

            (window as any).BrTrk?.getTracker().updateBrData(tmpBrData);
            (window as any).BrTrk?.getTracker().logPageView();
        }
    };

    useEffect(() => {
        if (isFirst) {
            if (bloomreachSettings?.accountId && currentLanguage) {
                const tmpBrData = getBloomreachData(currentPage);
                (window as any).br_data = tmpBrData;

                const brtrk = document.createElement("script");
                brtrk.type = "text/javascript";
                brtrk.async = true;
                brtrk.src = `//cdn.brcdn.com/v1/br-trk-${bloomreachSettings?.accountId}.js`;
                const s = document.getElementsByTagName("script")[0];
                s.parentNode?.insertBefore(brtrk, s);

                setIsFirst(false);
            }
        }
    }, [bloomreachSettings, currentLanguage]);

    useEffect(() => {
        if (!isFirst) {
            if (catalogPage && category) {
                if (isWaitingForState) {
                    setIsWaitingForState(false);
                    trackVirtualPageView();
                }
            }
        }
    }, [catalogPage, category, isCategoryPage]);

    useEffect(() => {
        if (!isFirst) {
            if (orderConfirmationCart) {
                if (isWaitingForState) {
                    setIsWaitingForState(false);
                    trackVirtualPageView();
                }
            }
        }
    }, [orderConfirmationCart]);

    useEffect(() => {
        if (!isFirst) {
            if (isSearchPage) {
                if (isWaitingForState) {
                    setIsWaitingForState(false);
                    trackVirtualPageView();
                }
            }
        }
    }, [isSearchPage]);

    useEffect(() => {
        if (!isFirst) {
            if (productAttributesStyles) {
                if (isWaitingForState) {
                    setIsWaitingForState(false);
                    trackVirtualPageView();
                }
            }
        }
    }, [product]);

    useEffect(() => {
        if (!isFirst) {
            if (brand) {
                if (isWaitingForState) {
                    setIsWaitingForState(false);
                    trackVirtualPageView();
                }
            }
        }
    }, [brand]);

    useEffect(() => {
        if (!isFirst) {
            trackVirtualPageView();
        }
    }, [currentPath, currentSearch]);

    return null;
};

export default BloomreachPixel;
